import React, { useMemo } from 'react';
import { useGetCardAddSourcesQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod, CardAddSourceRow } from 'types/interfaces/admin/analytics';
import StackedBarChart, { StackedBarChartRow } from 'components/admin/charts/StackedBarChart';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

// Fixed source→color mapping shared across both charts so a source reads the same in each.
// (categorical-4 for the known surfaces; neutral gray for unknown.)
const SOURCE_COLORS: Record<string, string> = {
  collection: '#8B5CF6',
  'card-details': '#2DD4BF',
  search: '#F59E0B',
  'bulk-import': '#F472B6',
  unknown: '#6B7280',
};

const toRows = (rows?: CardAddSourceRow[]): StackedBarChartRow[] | undefined =>
  rows?.map((r) => ({ date: r.date, dimension: r.source, value: r.count }));

const CardAddSourcesCharts: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error, refetch } = useGetCardAddSourcesQuery({ period });

  const wantRows = useMemo(() => toRows(data?.wantList), [data]);
  const haveRows = useMemo(() => toRows(data?.haveList), [data]);

  return (
    <>
      <StackedBarChart
        title="Cards Added to Want List by Source"
        subtitle="Daily count by source surface (UTC)"
        period={period}
        onPeriodChange={onPeriodChange}
        data={wantRows}
        isLoading={isLoading}
        error={!!error}
        onRetry={refetch}
        colorPalette={SOURCE_COLORS}
        valueNoun="cards added"
        emptyMessage="No want-list adds in this period"
      />
      <StackedBarChart
        title="Cards Added to Have List by Source"
        subtitle="Daily count by source surface (UTC)"
        period={period}
        onPeriodChange={onPeriodChange}
        data={haveRows}
        isLoading={isLoading}
        error={!!error}
        onRetry={refetch}
        colorPalette={SOURCE_COLORS}
        valueNoun="cards added"
        emptyMessage="No have-list adds in this period"
      />
    </>
  );
};

export default CardAddSourcesCharts;
